import { ChevronRight, ScreenShare, Smartphone } from "lucide-react"
import { memo, useCallback } from "react"
import { setPreviewModal } from "../../../../../redux/slices/integrations"
import { Integration } from "../../../../../types"
import { useAppDispatch } from "../../../../hooks"
import { TYPE_LABELS, getFeatureTags } from "../helpers"
import IntegrationStatusTag from "../components/IntegrationStatusTag"
import IntegrationTypeIcon from "../components/IntegrationTypeIcon"

interface Props {
  data: Integration
  onSelect?: (id: string) => void
}

const IntegrationCard = memo(({ data, onSelect }: Props) => {
  const dispatch = useAppDispatch()
  const { title, type, app_key, id } = data
  const tags = getFeatureTags(data)
  const typeLabel = TYPE_LABELS[type] ?? type

  const openEditPanel = useCallback(() => {
    onSelect?.(id)
  }, [onSelect, id])

  const handlePreview = useCallback(
    (device: "desktop" | "mobile", e: React.MouseEvent) => {
      e.stopPropagation()
      dispatch(setPreviewModal({ isOpen: true, app_key, title, device }))
    },
    [dispatch, app_key, title]
  )

  return (
    <div
      className="w-full p-5 cursor-pointer"
      onClick={openEditPanel}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && openEditPanel()}
    >
      <div className="flex gap-4">
        <div className="flex-shrink-0">
          <IntegrationTypeIcon type={type} />
        </div>

        <div className="flex-grow min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span className="font-bold text-base text-base-dark leading-snug">
              {title}
            </span>
          </div>

          <p className="text-sm text-base-dark mt-0.5">{typeLabel}</p>

          <div className="mt-2 flex items-center gap-2">
            <IntegrationStatusTag integrationId={id} />
            <span className="text-sm text-base-dark">App key</span>
            <span className="bg-[#f5f5f5] px-2 py-0.5 font-mono text-sm text-base-dark whitespace-nowrap rounded-sm">
              {app_key}
            </span>
          </div>

          <div
            className="mt-4 flex items-center justify-end gap-4 border-t border-slate-100 pt-3"
            onClick={e => e.stopPropagation()}
          >
            {tags.length > 0 && (
              <div className="mr-auto flex flex-wrap gap-1.5">
                {tags.map(tag => (
                  <span
                    key={tag}
                    className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md px-2.5 py-1 text-xs font-medium text-[#343c49] whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {type !== "api" && (
              <>
                <button
                  type="button"
                  onClick={e => handlePreview("desktop", e)}
                  className="text-primary-400 hover:opacity-75 transition-opacity focus:outline-none"
                  aria-label="Desktop preview"
                >
                  <ScreenShare size={18} />
                </button>
                <button
                  type="button"
                  onClick={e => handlePreview("mobile", e)}
                  className="text-primary-400 hover:opacity-75 transition-opacity focus:outline-none"
                  aria-label="Mobile preview"
                >
                  <Smartphone size={18} />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                openEditPanel()
              }}
              aria-label="Open"
            >
              <ChevronRight className="text-neutral-400" size={22} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
})

IntegrationCard.displayName = "IntegrationCard"

export default IntegrationCard
